import { MouseAPI, KeyboardAPI, TouchAPI, TouchPoint } from '../types/index.js';

/**
 * Manages user interaction state and APIs for the worker runtime.
 * Handles mouse, keyboard, and touch input processing for Phase 7.
 * 
 * This system provides:
 * - Real-time interaction state management
 * - Frame-based event processing
 * - Clean API generation for artist code
 * - Message handling for interaction updates
 */
export class InteractionSystem {
  // Interaction enabled state
  private isEnabled: boolean = true;

  // Mouse interaction state
  private mouseState: MouseAPI = {
    x: 0, y: 0,
    isInCanvas: false,
    isPressed: false,
    leftButton: false,
    rightButton: false,
    middleButton: false,
    velocity: { x: 0, y: 0 },
    deltaX: 0,
    deltaY: 0,
    wheelDelta: 0,
    wheelX: 0,
    wheelY: 0,
    wasPressed: false,
    wasReleased: false,
    wasMoved: false
  };

  // Keyboard interaction state
  private keyboardState: KeyboardAPI = {
    isPressed: (key: string) => this.keyboardState.activeKeys.has(key.toLowerCase()),
    wasPressed: (key: string) => this.keyboardState.pressedThisFrame.has(key.toLowerCase()),
    wasReleased: (key: string) => this.keyboardState.releasedThisFrame.has(key.toLowerCase()),
    activeKeys: new Set<string>(),
    pressedThisFrame: new Set<string>(),
    releasedThisFrame: new Set<string>(),
    lastKeyPressed: '',
    lastKeyReleased: '',
    shift: false,
    ctrl: false,
    alt: false,
    meta: false
  };

  // Touch interaction state
  private touchState: TouchAPI = {
    points: [],
    count: 0,
    started: [],
    moved: [],
    ended: [],
    primary: null,
    gestures: {
      isPinching: false,
      isRotating: false,
      isPanning: false,
      isTapping: false,
      pinchScale: 1,
      pinchDelta: 0,
      rotationAngle: 0,
      rotationDelta: 0,
      panDelta: { x: 0, y: 0 },
      tapCount: 0,
      lastTapTime: 0,
      tapPosition: null
    }
  };

  constructor() {
    // Bind methods for proper context when used as callbacks
    this.handleMouseUpdate = this.handleMouseUpdate.bind(this);
    this.handleKeyboardUpdate = this.handleKeyboardUpdate.bind(this);
    this.handleTouchUpdate = this.handleTouchUpdate.bind(this);
    this.frameStart = this.frameStart.bind(this);
  }

  /**
   * Get the interaction APIs for inclusion in the viji object
   */
  public getInteractionAPIs() {
    return {
      mouse: this.mouseState,
      keyboard: this.keyboardState,
      touches: this.touchState
    };
  }

  /**
   * Called at the start of each frame to reset frame-based events
   */
  public frameStart(): void {
    // Reset mouse frame events
    this.mouseState.wasPressed = false;
    this.mouseState.wasReleased = false;
    this.mouseState.wasMoved = false;
    this.mouseState.wheelDelta = 0;
    this.mouseState.wheelX = 0;
    this.mouseState.wheelY = 0;

    // Reset keyboard frame events
    this.keyboardState.pressedThisFrame.clear();
    this.keyboardState.releasedThisFrame.clear();

    // Reset touch frame events
    this.touchState.started = [];
    this.touchState.moved = [];
    this.touchState.ended = [];
    this.touchState.gestures.isTapping = false;
    this.touchState.gestures.pinchDelta = 0;
    this.touchState.gestures.rotationDelta = 0;
  }

  /**
   * Handle mouse update messages from the host
   */
  public handleMouseUpdate(data: {
    x: number;
    y: number;
    buttons: number;
    deltaX: number;
    deltaY: number;
    wheelDeltaX: number;
    wheelDeltaY: number;
    isInCanvas?: boolean;
    timestamp: number;
    wasPressed?: boolean;
    wasReleased?: boolean;
  }): void {
    // Skip processing if interactions are disabled
    if (!this.isEnabled) return;
    // Update mouse position and button states
    this.mouseState.x = data.x;
    this.mouseState.y = data.y;
    this.mouseState.isInCanvas = data.isInCanvas !== undefined ? data.isInCanvas : true;
    this.mouseState.leftButton = (data.buttons & 1) !== 0;
    this.mouseState.rightButton = (data.buttons & 2) !== 0;
    this.mouseState.middleButton = (data.buttons & 4) !== 0;
    this.mouseState.isPressed = data.buttons > 0;
    
    // Update movement deltas and wheel
    this.mouseState.deltaX = data.deltaX || 0;
    this.mouseState.deltaY = data.deltaY || 0;
    this.mouseState.wheelDelta = data.wheelDeltaY || 0;
    this.mouseState.wheelX = data.wheelDeltaX || 0;
    this.mouseState.wheelY = data.wheelDeltaY || 0;
    
    // Update velocity (smoothed in InteractionManager)
    this.mouseState.velocity.x = data.deltaX || 0;
    this.mouseState.velocity.y = data.deltaY || 0;
    
    // Frame-based events (calculated in InteractionManager or provided by host)
    this.mouseState.wasPressed = data.wasPressed || false;
    this.mouseState.wasReleased = data.wasReleased || false;
    this.mouseState.wasMoved = (data.deltaX !== 0 || data.deltaY !== 0);
  }

  /**
   * Handle keyboard update messages from the host
   */
  public handleKeyboardUpdate(data: {
    type: 'keydown' | 'keyup';
    key: string;
    code: string;
    shiftKey: boolean;
    ctrlKey: boolean;
    altKey: boolean;
    metaKey: boolean;
    timestamp: number;
  }): void {
    // Skip processing if interactions are disabled
    if (!this.isEnabled) return;
    const key = data.key.toLowerCase();
    
    if (data.type === 'keydown') {
      if (!this.keyboardState.activeKeys.has(key)) {
        this.keyboardState.activeKeys.add(key);
        this.keyboardState.pressedThisFrame.add(key);
        this.keyboardState.lastKeyPressed = data.key;
      }
    } else if (data.type === 'keyup') {
      this.keyboardState.activeKeys.delete(key);
      this.keyboardState.releasedThisFrame.add(key);
      this.keyboardState.lastKeyReleased = data.key;
    }
    
    // Update modifier keys
    this.keyboardState.shift = data.shiftKey;
    this.keyboardState.ctrl = data.ctrlKey;
    this.keyboardState.alt = data.altKey;
    this.keyboardState.meta = data.metaKey;
  }

  /**
   * Handle touch update messages from the host
   */
  public handleTouchUpdate(data: {
    type: 'touchstart' | 'touchmove' | 'touchend' | 'touchcancel';
    touches: Array<{
      identifier: number;
      clientX: number;
      clientY: number;
      pressure: number;
      radiusX: number;
      radiusY: number;
      rotationAngle: number;
      force: number;
    }>;
    timestamp: number;
  }): void {
    // Skip processing if interactions are disabled
    if (!this.isEnabled) return;
    // Clear frame-based touch events
    this.touchState.started = [];
    this.touchState.moved = [];
    this.touchState.ended = [];
    
    // Convert touch data to our TouchPoint format
    const touches: TouchPoint[] = data.touches.map((touch) => ({
      id: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
      pressure: touch.pressure || 0,
      radius: Math.max(touch.radiusX || 0, touch.radiusY || 0),
      radiusX: touch.radiusX || 0,
      radiusY: touch.radiusY || 0,
      rotationAngle: touch.rotationAngle || 0,
      force: touch.force || touch.pressure || 0,
      deltaX: 0, // Could be calculated if we track previous positions
      deltaY: 0,
      velocity: { x: 0, y: 0 }, // Could be calculated if we track movement
      isNew: data.type === 'touchstart',
      isActive: true,
      isEnding: data.type === 'touchend' || data.type === 'touchcancel'
    }));
    
    // Update touch state
    this.touchState.points = touches;
    this.touchState.count = touches.length;
    this.touchState.primary = touches[0] || null;
    
    // Set frame-based events based on message type
    if (data.type === 'touchstart') {
      this.touchState.started = touches;
    } else if (data.type === 'touchmove') {
      this.touchState.moved = touches;
    } else if (data.type === 'touchend' || data.type === 'touchcancel') {
      this.touchState.ended = touches;
    }
    
    // Note: Advanced gesture recognition is handled in the InteractionManager
    // on the host side. For now, we provide basic gesture state structure.
    // In the future, we could move gesture recognition here or receive 
    // gesture data from the host.
    this.touchState.gestures = {
      isPinching: false,
      isRotating: false,
      isPanning: false,
      isTapping: false,
      pinchScale: 1,
      pinchDelta: 0,
      rotationAngle: 0,
      rotationDelta: 0,
      panDelta: { x: 0, y: 0 },
      tapCount: 0,
      lastTapTime: 0,
      tapPosition: null
    };
  }

  /**
   * Reset all interaction state (called when loading new scene)
   */
  public resetInteractionState(): void {
    // Reset mouse state
    Object.assign(this.mouseState, {
      x: 0, y: 0,
      isInCanvas: false,
      isPressed: false,
      leftButton: false,
      rightButton: false,
      middleButton: false,
      velocity: { x: 0, y: 0 },
      deltaX: 0,
      deltaY: 0,
      wheelDelta: 0,
      wheelX: 0,
      wheelY: 0,
      wasPressed: false,
      wasReleased: false,
      wasMoved: false
    });

    // Reset keyboard state
    this.keyboardState.activeKeys.clear();
    this.keyboardState.pressedThisFrame.clear();
    this.keyboardState.releasedThisFrame.clear();
    this.keyboardState.lastKeyPressed = '';
    this.keyboardState.lastKeyReleased = '';
    this.keyboardState.shift = false;
    this.keyboardState.ctrl = false;
    this.keyboardState.alt = false;
    this.keyboardState.meta = false;

    // Reset touch state
    this.touchState.points = [];
    this.touchState.count = 0;
    this.touchState.started = [];
    this.touchState.moved = [];
    this.touchState.ended = [];
    this.touchState.primary = null;
    Object.assign(this.touchState.gestures, {
      isPinching: false,
      isRotating: false,
      isPanning: false,
      isTapping: false,
      pinchScale: 1,
      pinchDelta: 0,
      rotationAngle: 0,
      rotationDelta: 0,
      panDelta: { x: 0, y: 0 },
      tapCount: 0,
      lastTapTime: 0,
      tapPosition: null
    });
  }

  /**
   * Enable or disable interaction processing
   */
  public setInteractionEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    
    // If disabled, reset all interaction states to prevent stale data
    if (!enabled) {
      this.resetInteractionStates();
    }
  }

  /**
   * Get current interaction enabled state
   */
  public getInteractionEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Reset all interaction states to default values
   */
  private resetInteractionStates(): void {
    // Reset mouse state
    this.mouseState.x = 0;
    this.mouseState.y = 0;
    this.mouseState.isInCanvas = false;
    this.mouseState.isPressed = false;
    this.mouseState.leftButton = false;
    this.mouseState.rightButton = false;
    this.mouseState.middleButton = false;
    this.mouseState.velocity.x = 0;
    this.mouseState.velocity.y = 0;
    this.mouseState.deltaX = 0;
    this.mouseState.deltaY = 0;
    this.mouseState.wheelDelta = 0;
    this.mouseState.wheelX = 0;
    this.mouseState.wheelY = 0;
    this.mouseState.wasPressed = false;
    this.mouseState.wasReleased = false;
    this.mouseState.wasMoved = false;

    // Reset keyboard state
    this.keyboardState.activeKeys.clear();
    this.keyboardState.pressedThisFrame.clear();
    this.keyboardState.releasedThisFrame.clear();
    this.keyboardState.lastKeyPressed = '';
    this.keyboardState.lastKeyReleased = '';
    this.keyboardState.shift = false;
    this.keyboardState.ctrl = false;
    this.keyboardState.alt = false;
    this.keyboardState.meta = false;

    // Reset touch state
    this.touchState.points = [];
    this.touchState.count = 0;
    this.touchState.started = [];
    this.touchState.moved = [];
    this.touchState.ended = [];
    this.touchState.primary = null;
    this.touchState.gestures.isPinching = false;
    this.touchState.gestures.isRotating = false;
    this.touchState.gestures.isPanning = false;
    this.touchState.gestures.isTapping = false;
    this.touchState.gestures.pinchScale = 1;
    this.touchState.gestures.pinchDelta = 0;
    this.touchState.gestures.rotationAngle = 0;
    this.touchState.gestures.rotationDelta = 0;
    this.touchState.gestures.panDelta = { x: 0, y: 0 };
    this.touchState.gestures.tapCount = 0;
    this.touchState.gestures.lastTapTime = 0;
    this.touchState.gestures.tapPosition = null;
  }
} 